// ============================================================
// Commandery Access Control Panel · /square/projects/:id/client-access
//
// The operator surface where Jordan controls Commandery / Secure Share access
// from Square Code. Square Code is the OPERATOR control panel; Olivium / Secure
// Share is the ACCESS AUTHORITY. This panel renders the controls (create access,
// invite list, permissions, one-time reveal design, planned sessions, activity)
// and stays HONEST: real invite creation is gated behind the Olivium Secure Share
// backend connection, so create/revoke/expire actions are clearly gated until
// that route is wired + approved. No service-role keys, no hashes, no raw errors.
// See docs/client-connect/COMMANDERY_ACCESS_CONTROL_PANEL.md
// ============================================================

import { useState } from 'react'
import { useParams } from 'react-router-dom'

import ProjectSurface from './ProjectSurface'
import { useProjects } from '../../hooks/useProjects'
import { getRuntime } from '../../hooks/_mode'
import { SHELL_TOKENS, chipStyle, statusAccent, surfaces, text } from '../../shell/styles'
import {
  ACCESS_MODES, ACCESS_TYPES, EXPIRATION_OPTIONS, GRANT_LABEL, LIVE_MODE_LABEL,
  PERMISSION_MATRIX, PROTECTION_STATES, RESOURCES, ROLES, VISIBILITY,
  createAccessInviteDryRun, createAccessInviteLive, inviteStatusTone, previewInvites, protectionForStatus,
  type AccessMode, type AccessType, type CreateAccessResult, type Expiration,
  type Grant, type Role, type Visibility,
} from './accessControl'

const TABS = ['Overview', 'Invites', 'Create Access', 'Permissions', 'Sessions', 'Activity'] as const
type Tab = (typeof TABS)[number]

function PlannedPill() {
  return <span style={chipStyle(SHELL_TOKENS.inkFaint)}>Planned</span>
}

function grantColor(g: Grant): string {
  if (g === 'full') return SHELL_TOKENS.green
  if (g === 'read') return SHELL_TOKENS.blue
  if (g === 'request') return SHELL_TOKENS.gold
  if (g === 'planned') return SHELL_TOKENS.inkFaint
  return SHELL_TOKENS.inkFaint
}

export default function ClientAccess() {
  const { id } = useParams<{ id: string }>()
  const { projects } = useProjects()
  const project = projects.find((p) => p.id === id)
  const { live } = getRuntime()
  const [tab, setTab] = useState<Tab>('Overview')

  const invites = live ? [] : previewInvites()
  const count = (s: string) => invites.filter((i) => i.status === s).length

  return (
    <ProjectSurface
      kicker="Commandery · Client Access"
      title="Access Control Panel"
      description="Operator controls for Commandery / Secure Share. Olivium is the access authority; actions stay gated until that connection is enabled."
    >
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              padding: '7px 13px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${tab === t ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
              background: tab === t ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
              color: tab === t ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
              fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-mono)',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <OverviewTab counts={{ active: count('active'), pending: count('pending'), redeemed: count('redeemed'), revoked: count('revoked') }} live={live} />}
      {tab === 'Invites' && <InvitesTab invites={invites} projectName={project?.name ?? '—'} live={live} />}
      {tab === 'Create Access' && <CreateAccessTab projectId={id ?? ''} projectName={project?.name ?? '—'} />}
      {tab === 'Permissions' && <PermissionsTab />}
      {tab === 'Sessions' && <SessionsTab />}
      {tab === 'Activity' && <ActivityTab live={live} />}
    </ProjectSurface>
  )
}

// ── Overview ──
function OverviewTab({ counts, live }: { counts: Record<string, number>; live: boolean }) {
  const statusRows: Array<{ name: string; tone: string; label: string }> = [
    { name: 'Secure Share', tone: SHELL_TOKENS.cyan, label: 'source-ready' },
    { name: 'Secure Shield', tone: SHELL_TOKENS.cyan, label: 'source-ready' },
    { name: 'Phase readiness', tone: SHELL_TOKENS.blue, label: 'Phase 4 live · 5–6 merged · 7 planned' },
  ]
  const metrics: Array<{ label: string; value: string; planned?: boolean }> = [
    { label: 'Active invites', value: String(counts.active) },
    { label: 'Pending invites', value: String(counts.pending) },
    { label: 'Redeemed invites', value: String(counts.redeemed) },
    { label: 'Revoked invites', value: String(counts.revoked) },
    { label: 'Active sessions', value: '—', planned: true },
    { label: 'Protected activity', value: '—', planned: true },
  ]
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={surfaces.card}>
        <div style={{ ...text.kicker, marginBottom: 12 }}>Protection status</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {statusRows.map((r) => (
            <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={text.body}>{r.name}</span>
              <span style={chipStyle(r.tone)}>{r.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {metrics.map((m) => (
          <div key={m.label} style={surfaces.card}>
            <div style={{ ...text.dataValue, color: m.planned ? SHELL_TOKENS.inkFaint : SHELL_TOKENS.ink }}>{m.value}</div>
            <div style={{ ...text.rowMeta, marginTop: 4 }}>{m.label}</div>
            {m.planned && <div style={{ marginTop: 8 }}><PlannedPill /></div>}
          </div>
        ))}
      </div>
      {!live && (
        <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.brandLine, background: SHELL_TOKENS.brandSoft }}>
          <span style={text.body}>Preview mode — counts reflect sample invites, not live data.</span>
        </div>
      )}
    </div>
  )
}

// ── Invites ──
function InvitesTab({ invites, projectName, live }: { invites: ReturnType<typeof previewInvites>; projectName: string; live: boolean }) {
  if (invites.length === 0) {
    return (
      <div style={{ ...surfaces.card, textAlign: 'center', padding: 32 }}>
        <div style={{ ...text.sectionTitle, marginBottom: 6 }}>No invites yet</div>
        <div style={text.rowMeta}>
          {live
            ? 'Invites appear here once access creation is connected to Olivium Secure Share.'
            : 'Preview mode has no sample invites for this view.'}
        </div>
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {invites.map((inv) => {
        const prot = protectionForStatus(inv.status)
        return (
          <div key={inv.id} style={{ ...surfaces.row, padding: 14, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={text.rowTitle}>{inv.recipient}</span>
              <span style={chipStyle(statusAccent(inviteStatusTone(inv.status)))}>{inv.status}</span>
            </div>
            <div style={{ ...text.rowMeta, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>{projectName}</span>
              <span>type: {inv.accessType}</span>
              <span>role: {inv.role}</span>
              <span>mode: {inv.mode}</span>
              <span>created {inv.created}</span>
              <span>expires {inv.expires}</span>
              <span>redeemed {inv.redeemed ?? '—'}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={chipStyle(SHELL_TOKENS.green)}>◆ {PROTECTION_STATES[prot]}</span>
              <span style={{ flex: 1 }} />
              <GatedAction label="Copy" />
              <GatedAction label="Revoke" />
              <GatedAction label="Expire" />
            </div>
          </div>
        )
      })}
      {!live && <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint }}>Preview rows — not live invites.</div>}
    </div>
  )
}

function GatedAction({ label }: { label: string }) {
  return (
    <button
      type="button"
      disabled
      title="Connect Olivium Secure Share to enable this action."
      style={{
        padding: '5px 11px', borderRadius: 7, border: `1px solid ${SHELL_TOKENS.border}`,
        background: SHELL_TOKENS.surface, color: SHELL_TOKENS.inkFaint, fontSize: 11,
        fontWeight: 600, fontFamily: 'var(--font-mono)', cursor: 'not-allowed', opacity: 0.7,
      }}
    >
      {label}
    </button>
  )
}

// ── Create Access ──
function CreateAccessTab({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [label, setLabel] = useState('')
  const [recipient, setRecipient] = useState('')
  const [accessType, setAccessType] = useState<AccessType>('workspace')
  const [role, setRole] = useState<Role>('viewer')
  const [visibility, setVisibility] = useState<Visibility>('client_visible')
  const [expiration, setExpiration] = useState<Expiration>('24h')
  const [mode, setMode] = useState<AccessMode>('live_client')
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState<CreateAccessResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [execMode, setExecMode] = useState<'dry_run' | 'live'>('dry_run')

  async function onCreate() {
    setBusy(true)
    setResult(null)
    const input = { projectId, label, recipient, accessType, role, visibility, expiration, mode, notes }
    // Dry run returns a clearly-flagged mock (no invite/code created). Live calls
    // Olivium ss-invite-create SERVER-SIDE and returns a ONE-TIME access code.
    // Both go through /api/access/create, where operator auth + ownership are
    // enforced; live fails closed if config/mapping/identity is missing.
    const r = execMode === 'live'
      ? await createAccessInviteLive(input)
      : await createAccessInviteDryRun(input)
    setResult(r)
    setBusy(false)
  }

  const field = (labelText: string, node: React.ReactNode) => (
    <label style={{ display: 'grid', gap: 5 }}>
      <span style={text.rowMeta}>{labelText}</span>
      {node}
    </label>
  )
  const inputStyle: React.CSSProperties = {
    background: SHELL_TOKENS.surfaceAlt, border: `1px solid ${SHELL_TOKENS.border}`, borderRadius: 8,
    padding: '8px 10px', color: SHELL_TOKENS.ink, fontSize: 13, fontFamily: 'var(--font-mono)',
  }
  const sel = <T extends string>(val: T, set: (v: T) => void, opts: ReadonlyArray<{ key: T; label: string }>) => (
    <select value={val} onChange={(e) => set(e.target.value as T)} style={inputStyle}>
      {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  )

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={surfaces.card}>
        <div style={{ ...text.kicker, marginBottom: 12 }}>Create access · {projectName}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {field('Access label', <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Tester invite" style={inputStyle} />)}
          {field('Recipient (optional)', <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="name or email" style={inputStyle} />)}
          {field('Access type', sel(accessType, setAccessType, ACCESS_TYPES))}
          {field('Role', sel(role, setRole, ROLES))}
          {field('Visibility', sel(visibility, setVisibility, VISIBILITY))}
          {field('Expiration', sel(expiration, setExpiration, EXPIRATION_OPTIONS))}
          {field('Access mode', sel(mode, setMode, ACCESS_MODES))}
          {field('Notes', <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="internal note" style={inputStyle} />)}
        </div>
        <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint, marginTop: 8 }}>
          {ACCESS_MODES.find((m) => m.key === mode)?.note}
        </div>
        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          {/* Execution mode — Dry run (safe mock) vs Live Olivium Access (real). */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={text.rowMeta}>Execution</span>
            {([['dry_run', 'Dry run'], ['live', 'Live Olivium Access']] as const).map(([key, lbl]) => (
              <button
                key={key} type="button" onClick={() => setExecMode(key)}
                style={{
                  padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  fontFamily: 'var(--font-mono)',
                  border: `1px solid ${execMode === key ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
                  background: execMode === key ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
                  color: execMode === key ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
                }}
              >
                {lbl}
              </button>
            ))}
          </div>

          {execMode === 'live' && (
            <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.brandLine, background: SHELL_TOKENS.brandSoft, padding: '10px 12px' }}>
              <div style={{ ...text.rowMeta, color: SHELL_TOKENS.brand }}>{LIVE_MODE_LABEL}</div>
              <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint, marginTop: 6 }}>
                Only the access label is sent to Olivium in this phase. Access type, role, visibility,
                expiration, and mode stay operator metadata and are not enforced by Olivium yet. If live
                access is not connected for this environment or session, you'll get a clear message and
                nothing is created.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button" onClick={onCreate} disabled={busy}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${SHELL_TOKENS.brandLine}`,
                background: SHELL_TOKENS.brandSoft, color: SHELL_TOKENS.brand, fontSize: 12, fontWeight: 700,
                fontFamily: 'var(--font-mono)', cursor: busy ? 'wait' : 'pointer',
              }}
            >
              {busy ? 'Running…' : execMode === 'live' ? 'Create live Olivium access' : 'Run dry-run create'}
            </button>
            <GatedAction label="Generate one-time code" />
            <GatedAction label="Copy secure link" />
            {execMode === 'live'
              ? <span style={chipStyle(SHELL_TOKENS.gold)}>Live — creates a real invite</span>
              : <span style={chipStyle(SHELL_TOKENS.cyan)}>Dry run — no live access created</span>}
          </div>
        </div>
      </div>

      {result && <CreateResult result={result} />}

      <RevealDesignPreview />
    </div>
  )
}

// Create Access result — dry-run mock, gated, or a safe generic error. Never
// shows a real code/token/hash (dry run creates none).
function CreateResult({ result }: { result: CreateAccessResult }) {
  if (result.ok && 'live' in result && result.live) {
    return <LiveReveal result={result} />
  }
  if (result.ok && 'dry_run' in result && result.dry_run) {
    return (
      <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.brandLine }} role="status">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={chipStyle(SHELL_TOKENS.cyan)}>Dry run</span>
          <span style={text.rowTitle}>No live access created</span>
        </div>
        <div style={text.body}>{result.message}</div>
        <div style={{ ...text.rowMeta, display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
          <span>placeholder id: {result.invite.id}</span>
          <span>status: {result.invite.status}</span>
        </div>
        <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint, marginTop: 8 }}>
          Mock only — no invite, access code, token, or hash was created. Real creation stays gated until Olivium Secure Share is wired.
        </div>
      </div>
    )
  }
  if (!result.ok && 'gated' in result) {
    return (
      <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.brandLine, background: SHELL_TOKENS.brandSoft }} role="status">
        <div style={{ ...text.rowTitle, marginBottom: 4 }}>Backend connection required</div>
        <div style={text.body}>{result.message}</div>
      </div>
    )
  }
  return (
    <div style={{ ...surfaces.card }} role="status">
      <div style={{ ...text.rowTitle, marginBottom: 4 }}>Couldn’t run</div>
      <div style={text.body}>{result.message}</div>
    </div>
  )
}

// LIVE one-time reveal — shows the REAL one-time access code returned by Olivium
// exactly once, with a copy button and the unrecoverable warning. Renders ONLY
// safe fields (invite id, status, code_reveal, code_storage, the plaintext code).
// Never renders any *-hash field, a session token or binding, backend debug,
// env names, or any service-role material — the server strips those.
function LiveReveal({ result }: { result: Extract<CreateAccessResult, { live: true }> }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(result.access_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard unavailable — operator can select the code manually */ }
  }
  return (
    <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.brandLine }} role="status">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={chipStyle(SHELL_TOKENS.gold)}>Live</span>
        <span style={text.rowTitle}>Access created</span>
      </div>
      <div style={{ ...text.rowMeta, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>invite id: {result.invite.id}</span>
        <span>status: {result.invite.status}</span>
        <span>reveal: {result.code_reveal}</span>
        <span>storage: {result.code_storage}</span>
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginTop: 10,
        background: SHELL_TOKENS.void, border: `1px solid ${SHELL_TOKENS.border}`,
      }}>
        <code style={{ ...text.code, letterSpacing: '0.14em', color: SHELL_TOKENS.ink }}>{result.access_code}</code>
        <span style={{ flex: 1 }} />
        <button
          type="button" onClick={copy}
          style={{
            padding: '5px 11px', borderRadius: 7, border: `1px solid ${SHELL_TOKENS.brandLine}`,
            background: SHELL_TOKENS.brandSoft, color: SHELL_TOKENS.brand, fontSize: 11, fontWeight: 600,
            fontFamily: 'var(--font-mono)', cursor: 'pointer',
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div style={{ ...text.rowMeta, marginTop: 10, color: SHELL_TOKENS.amberText }}>
        ⚠ This code is shown once and cannot be recovered.
      </div>
      <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint, marginTop: 8 }}>
        {result.message} Internal hashes and session tokens are never shown.
      </div>
    </div>
  )
}

// One-time code / link reveal — DESIGN PREVIEW only. Never a real code, never a
// hash. Shows the copy affordance + the one-time warning the wired flow will use.
function RevealDesignPreview() {
  return (
    <div style={{ ...surfaces.card, borderStyle: 'dashed' }}>
      <div style={{ ...text.kicker, marginBottom: 10 }}>One-time reveal · design preview</div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8,
        background: SHELL_TOKENS.void, border: `1px solid ${SHELL_TOKENS.border}`,
      }}>
        <code style={{ ...text.code, letterSpacing: '0.14em', color: SHELL_TOKENS.inkFaint }}>OLV-XXXX-XXXX-XXXX</code>
        <span style={{ flex: 1 }} />
        <GatedAction label="Copy" />
      </div>
      <div style={{ ...text.rowMeta, marginTop: 10, color: SHELL_TOKENS.amberText }}>
        ⚠ Save this now. The code will not be shown again.
      </div>
      {/* Never render any internal *-hash fields (access-code / session-token /
          binding) here — only the one-time plaintext code, once, when the wired
          flow returns it. */}
      <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint, marginTop: 8 }}>
        Placeholder only — a real one-time code appears here once, when the wired flow creates an invite.
        Internal hashes are never shown.
      </div>
    </div>
  )
}

// ── Permissions matrix ──
function PermissionsTab() {
  return (
    <div style={{ ...surfaces.card, overflowX: 'auto' }}>
      <div style={{ ...text.kicker, marginBottom: 12 }}>Permission matrix</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={thCell()}>Resource</th>
            {ROLES.map((r) => <th key={r.key} style={thCell()}>{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {RESOURCES.map((res) => (
            <tr key={res}>
              <td style={{ ...tdCell(), color: SHELL_TOKENS.inkDim }}>{res}</td>
              {ROLES.map((r) => {
                const g = PERMISSION_MATRIX[r.key][res]
                return <td key={r.key} style={tdCell()}><span style={chipStyle(grantColor(g))}>{GRANT_LABEL[g]}</span></td>
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
function thCell(): React.CSSProperties {
  return { ...text.micro, color: SHELL_TOKENS.inkFaint, textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${SHELL_TOKENS.borderAlt}` }
}
function tdCell(): React.CSSProperties {
  return { padding: '8px 10px', borderBottom: `1px solid ${SHELL_TOKENS.border}`, fontSize: 12, fontFamily: 'var(--font-mono)' }
}

// ── Sessions (planned) ──
function SessionsTab() {
  const cols = ['Session', 'Last seen', 'Idle', 'Suspicious opens', 'Receipt', 'Actions']
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ ...surfaces.card, borderColor: SHELL_TOKENS.borderAlt }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={text.sectionTitle}>Active sessions</span>
          <PlannedPill />
        </div>
        <div style={text.body}>
          Live session monitoring — last seen, idle state, revoke/lock, suspicious opens, and the receipt
          timeline — arrives with Secure Share Phase 8/9. This panel is source-ready and intentionally not wired yet.
        </div>
        <div style={{ ...text.rowMeta, display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, opacity: 0.6 }}>
          {cols.map((c) => <span key={c}>{c}</span>)}
        </div>
      </div>
    </div>
  )
}

// ── Activity / receipts ──
function ActivityTab({ live }: { live: boolean }) {
  const events = live ? [] : [
    { at: '2026-06-02', kind: 'INVITE_ISSUED', note: 'Preview — tester invite issued' },
    { at: '2026-06-01', kind: 'ACCESS_REDEEMED', note: 'Preview — client owner redeemed access' },
  ]
  if (events.length === 0) {
    return (
      <div style={{ ...surfaces.card, textAlign: 'center', padding: 32 }}>
        <div style={{ ...text.sectionTitle, marginBottom: 6 }}>No activity yet</div>
        <div style={text.rowMeta}>Access receipts appear here once the Olivium Secure Share connection is enabled.</div>
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {events.map((e, i) => (
        <div key={i} style={{ ...surfaces.row, padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={chipStyle(SHELL_TOKENS.cyan)}>{e.kind}</span>
          <span style={text.body}>{e.note}</span>
          <span style={{ flex: 1 }} />
          <span style={text.rowMeta}>{e.at}</span>
        </div>
      ))}
      {!live && <div style={{ ...text.micro, color: SHELL_TOKENS.inkFaint }}>Preview events — receipt-ready shape, not live.</div>}
    </div>
  )
}
